// frontend/src/components/BalanceAddModal.js
// Modal component for adding balance/YBT data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * BalanceAddModal Component
 * Provides a dedicated interface for adding Y-Balance Test (YBT) data to the flowsheet
 */
const BalanceAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one balance measurement.');
        return;
      }



      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Balance data saved successfully!');

        // Refresh vitals banner (in case balance data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save balance data');
      }
    } catch (error) {
      console.error('Error saving balance data:', error);
      message.error(error.response?.data?.message || 'Failed to save balance data. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Add Balance Data</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
        >
          Save Balance Data
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Balance Entry"
          description="Enter Y-Balance Test (YBT) measurements for the selected date and time. Limb length measurements are required to automatically calculate composite reach percentages, performance ratio, and deviation parameters."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="balance_add_form">
        <Title level={5}>Y-Balance Test Measurements</Title>
        
        {/* Trial 1 Measurements */}
        <Divider orientation="left">Trial 1</Divider>
        <Row gutter={16}>
          {/* Anterior Reach Trial 1 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_anterior_1" label="Right Anterior 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_anterior_1" label="Left Anterior 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>

          {/* Posteromedial Reach Trial 1 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_posteromedial_1" label="Right Posteromedial 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_posteromedial_1" label="Left Posteromedial 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>

          {/* Posterolateral Reach Trial 1 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_posterolateral_1" label="Right Posterolateral 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_posterolateral_1" label="Left Posterolateral 1 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Trial 2 Measurements */}
        <Divider orientation="left">Trial 2 (Optional)</Divider>
        <Row gutter={16}>
          {/* Anterior Reach Trial 2 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_anterior_2" label="Right Anterior 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_anterior_2" label="Left Anterior 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>

          {/* Posteromedial Reach Trial 2 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_posteromedial_2" label="Right Posteromedial 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_posteromedial_2" label="Left Posteromedial 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>

          {/* Posterolateral Reach Trial 2 */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_posterolateral_2" label="Right Posterolateral 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_posterolateral_2" label="Left Posterolateral 2 (cm)">
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter value"
                min={0}
                max={200}
                precision={1}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Limb Length Measurements */}
        <Divider orientation="left">Limb Length (Required for Calculations)</Divider>
        <Row gutter={16}>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_right_limb_length" label="Right Limb Length (cm)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter length"
                min={50}
                max={150}
                precision={1}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Form.Item name="biometrics_balance_ybt_left_limb_length" label="Left Limb Length (cm)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter length"
                min={50}
                max={150}
                precision={1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Alert
          message="Automatic Calculations"
          description="When limb length and individual measurements are provided, the system will automatically calculate: composite reach percentages, performance ratio, deviation parameters, and average values."
          type="success"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default BalanceAddModal;
